'use client';

import { Box } from '@zillow/constellation-10';
import { css } from '@/styled-system/css';
import {
  PRICE_HISTOGRAM_BUCKET_COUNT,
  PRICE_HISTOGRAM_BUCKET_SIZE,
  PRICE_HISTOGRAM_MAX,
  type PriceHistogram as PriceHistogramData,
} from '@/src/lib/api/endpoints/priceHistogram';

type PriceHistogramProps = {
  histogram: PriceHistogramData | undefined;
  /**
   * First fetch in flight. Draws the ghost chart so the popover doesn't reflow
   * when the bars land. Not set for refetches — those keep the previous data on
   * screen (`placeholderData` in usePriceHistogram).
   */
  isLoading?: boolean;
  /** Active price range in dollars; null on a side means that end is unbounded. */
  selection: { min: number | null; max: number | null };
  /** Applied to the root so the chart can inherit the slider's inline insets. */
  className?: string;
};

/** Keeps a one-listing bucket visible instead of rounding away to nothing. */
const MIN_BAR_HEIGHT_PERCENT = 2;

// Shared by the chart and its ghost so the two occupy an identical box and the
// swap moves nothing.
const chartBoxCss = css.raw({
  display: 'flex',
  alignItems: 'flex-end',
  // No gap between bars: 100 buckets across a 240px dropdown leaves ~2px each,
  // so the distribution has to read as a silhouette.
  height: '1400',
});

/**
 * Loading silhouette: a smooth hump, so what lands is a recolour rather than a
 * slab giving way to a sparse chart. Capped below full height and deliberately
 * free of any jitter — it should read as "a chart is coming", never as data.
 */
const GHOST_BAR_HEIGHTS = Array.from({ length: PRICE_HISTOGRAM_BUCKET_COUNT }, (_, index) => {
  const peak = 38;
  const spread = 22;
  return Math.round(Math.exp(-((index - peak) ** 2) / (2 * spread ** 2)) * 85);
});

/**
 * Rent distribution behind the price slider: one bar per $100 bucket, scaled to
 * the tallest bucket, with the buckets outside the active price range grayed out.
 *
 * Bars span the same box as the slider and are positioned by percentage, so
 * they line up with the handles as long as both share a container — Constellation's
 * slider maps value to position across its full width (see `Slider.ActiveTrack`).
 */
export default function PriceHistogram({ histogram, selection, isLoading, className }: PriceHistogramProps) {
  if (!histogram?.maxCount) {
    // Settled with nothing to chart (no listings, or no area bounds to ask
    // about) collapses rather than leaving a permanent empty band.
    if (!isLoading) return null;

    return (
      <Box aria-hidden="true" className={className} data-testid="price-histogram-placeholder" css={chartBoxCss}>
        {GHOST_BAR_HEIGHTS.map((height, index) => (
          <Box
            key={index * PRICE_HISTOGRAM_BUCKET_SIZE}
            css={{ flex: 1, minWidth: 0, backgroundColor: 'bg.softer' }}
            style={{ height: `${height}%` }}
          />
        ))}
      </Box>
    );
  }

  const { counts, maxCount } = histogram;
  const lowPrice = selection.min ?? 0;
  const highPrice = selection.max ?? PRICE_HISTOGRAM_MAX;

  return (
    <Box aria-hidden="true" className={className} data-testid="price-histogram" css={chartBoxCss}>
      {counts.map((count, index) => {
        const bucketStart = index * PRICE_HISTOGRAM_BUCKET_SIZE;
        // Highlight a bucket only when its whole span sits inside the range.
        // Testing the lower bound alone highlighted the bucket the max falls
        // on, painting $200-299 teal for a $200 cap. It also put colour to the
        // right of the handle: a handle at value V sits at V/PRICE_HISTOGRAM_MAX
        // across the track, which is exactly bucket V/100's left edge, so the
        // colour boundary and the handle line up only under this rule.
        const isInRange = bucketStart >= lowPrice && bucketStart + PRICE_HISTOGRAM_BUCKET_SIZE <= highPrice;

        return (
          <Box
            key={bucketStart}
            data-in-range={isInRange}
            css={{ flex: 1, minWidth: 0, backgroundColor: isInRange ? 'brand.primary' : 'bg.soft' }}
            style={{ height: count > 0 ? `${Math.max(MIN_BAR_HEIGHT_PERCENT, (count / maxCount) * 100)}%` : 0 }}
          />
        );
      })}
    </Box>
  );
}
